import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Select,
  Container,
  FormErrorMessage,
  Link,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/user';

interface FormData {
  fullName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  farmLocation?: string;
  farmSize?: string;
  farmName?: string;
  cropCategories?: string[];
  businessName?: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  password?: string;
  confirmPassword?: string;
  role?: string;
  farmLocation?: string;
  farmSize?: string;
  farmName?: string;
  cropCategories?: string;
  businessName?: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: (searchParams.get('type') as UserRole) || 'buyer',
    farmLocation: '',
    farmSize: '',
    farmName: '',
    cropCategories: [],
    businessName: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  
  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and a number';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (formData.phoneNumber.length > 15) {
      newErrors.phoneNumber = 'Phone number cannot exceed 15 characters';
    }
    
    if (formData.role === 'farmer' || formData.role === 'officer') {
      if (!formData.farmLocation) newErrors.farmLocation = 'Location is required';
    }

    if (formData.role === 'farmer') {

      if (!formData.farmName) newErrors.farmName = 'Farm name is required';
      if (!formData.cropCategories || formData.cropCategories.length === 0) newErrors.cropCategories = 'At least one crop category is required';
    }

    if (formData.role === 'buyer') {
      if (!formData.businessName) newErrors.businessName = 'Business name is required';
      if (!formData.farmLocation) newErrors.farmLocation = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { confirmPassword, ...userData } = formData;
      const normalizedUserData = {
        ...userData,
        farmSize: userData.farmSize ? Number(userData.farmSize) : undefined,
      };

      const result = await register(normalizedUserData);
      
      toast({
        title: result.emailSent ? 'Registration Successful' : 'Email Delivery Failed',
        description: result.message,
        status: result.emailSent ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });

      navigate('/verify-email', { state: { email: formData.email, emailSent: result.emailSent } });
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error instanceof Error ? error.message : 'An error occurred during registration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="white" display="flex" alignItems="center" py={8}>
      <Container maxW="500px" w="full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="12px"
            p={8}
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.05)"
          >
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <VStack spacing={2} align="start">
                <Heading
                  as="h1"
                  fontSize="28px"
                  fontWeight="700"
                  color="neutral.900"
                >
                  Create an account
                </Heading>
                <Text fontSize="14px" color="neutral.600" lineHeight="1.5">
                  Join MarketLink to start smart agricultural business
                </Text>
              </VStack>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  {/* User Type Selection */}
                  <FormControl isRequired isInvalid={!!errors.role}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      User Type
                    </FormLabel>
                    <Select
                      value={formData.role}
                      onChange={(e) => setFormData({
                        ...formData,
                        role: e.target.value as UserRole
                      })}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    >
                      <option value="farmer">🌾 Farmer</option>
                      <option value="buyer">🛒 Buyer</option>
                      <option value="officer">🍀 Agro Dealer</option>
                    </Select>
                    {errors.role && (
                      <FormErrorMessage fontSize="12px">{errors.role}</FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Full Name */}
                  <FormControl isRequired isInvalid={!!errors.fullName}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      Full Name
                    </FormLabel>
                    <Input
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={(e) => setFormData({
                        ...formData,
                        fullName: e.target.value
                      })}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(36, 149, 98, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.fullName && (
                      <FormErrorMessage fontSize="12px">{errors.fullName}</FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Email */}
                  <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      Email Address
                    </FormLabel>
                    <Input
                      type="email"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        email: e.target.value
                      })}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.email && (
                      <FormErrorMessage fontSize="12px">{errors.email}</FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Phone Number */}
                  <FormControl isRequired isInvalid={!!errors.phoneNumber}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      Phone Number
                    </FormLabel>
                    <Input
                      type="tel"
                      placeholder="+263 71 234 5678"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({
                        ...formData,
                        phoneNumber: e.target.value
                      })}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.phoneNumber && (
                      <FormErrorMessage fontSize="12px">{errors.phoneNumber}</FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Password */}
                  <FormControl isRequired isInvalid={!!errors.password}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      Password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({
                        ...formData,
                        password: e.target.value
                      })}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.password && (
                      <FormErrorMessage fontSize="12px">{errors.password}</FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Confirm Password */}
                  <FormControl isRequired isInvalid={!!errors.confirmPassword}>
                    <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                      Confirm Password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({
                        ...formData,
                        confirmPassword: e.target.value
                      })}
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: 'primary.500',
                        boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.confirmPassword && (
                      <FormErrorMessage fontSize="12px">{errors.confirmPassword}</FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Farmer-specific fields */}
                  {formData.role === 'farmer' && (
                    <>
                      <Box h="1px" bg="neutral.200" w="full" />
                      
                      <FormControl isRequired isInvalid={!!errors.farmLocation}>
                        <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                          Enter Location
                        </FormLabel>
                        <Input
                          placeholder="E.g., Harare, Zimbabwe"
                          value={formData.farmLocation}
                          onChange={(e) => setFormData({
                            ...formData,
                            farmLocation: e.target.value
                          })}
                          borderColor="neutral.200"
                          _focus={{
                            borderColor: 'primary.500',
                            boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                          }}
                          fontSize="14px"
                          h="40px"
                        />
                        {errors.farmLocation && (
                          <FormErrorMessage fontSize="12px">{errors.farmLocation}</FormErrorMessage>
                        )}
                      </FormControl>


                      
                      <FormControl isRequired isInvalid={!!errors.farmName}>
                        <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                          Farmland Name
                        </FormLabel>
                        <Input
                          placeholder="E.g., Sunrise Farms"
                          value={formData.farmName}
                          onChange={(e) => setFormData({
                            ...formData,
                            farmName: e.target.value
                          })}
                          borderColor="neutral.200"
                          _focus={{
                            borderColor: 'primary.500',
                            boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                          }}
                          fontSize="14px"
                          h="40px"
                        />
                        {errors.farmName && (
                          <FormErrorMessage fontSize="12px">{errors.farmName}</FormErrorMessage>
                        )}
                      </FormControl>
                      
                      <FormControl isRequired isInvalid={!!errors.cropCategories}>
                        <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                          Product Categories
                        </FormLabel>
                        <Select
                          placeholder="Select a category"
                          value={formData.cropCategories?.[0] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            cropCategories: [e.target.value]
                          })}
                          borderColor="neutral.200"
                          _focus={{
                            borderColor: 'primary.500',
                            boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                          }}
                          fontSize="14px"
                          h="40px"
                        >
                          <option value="vegetables">Vegetables</option>
                          <option value="fruits">Fruits</option>
                          <option value="grains">Grains</option>
                          <option value="livestock">Livestock</option>
                          <option value="dairy">Dairy</option>
                          <option value="other">Other</option>
                        </Select>
                        {errors.cropCategories && (
                          <FormErrorMessage fontSize="12px">{errors.cropCategories}</FormErrorMessage>
                        )}
                      </FormControl>
                    </>
                  )}

                  {/* Buyer-specific fields */}
                  {formData.role === 'buyer' && (
                    <>
                      <Box h="1px" bg="neutral.200" w="full" />
                      
                      <FormControl isRequired isInvalid={!!errors.businessName}>
                        <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                          Business Name
                        </FormLabel>
                        <Input
                          placeholder="E.g., Acme Agri Traders"
                          value={formData.businessName}
                          onChange={(e) => setFormData({
                            ...formData,
                            businessName: e.target.value
                          })}
                          borderColor="neutral.200"
                          _focus={{
                            borderColor: 'primary.500',
                            boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                          }}
                          fontSize="14px"
                          h="40px"
                        />
                        {errors.businessName && (
                          <FormErrorMessage fontSize="12px">{errors.businessName}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.farmLocation}>
                        <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                          Enter Location
                        </FormLabel>
                        <Input
                          placeholder="Harare, Zimbabwe"
                          value={formData.farmLocation}
                          onChange={(e) => setFormData({ ...formData, farmLocation: e.target.value })}
                          borderColor="neutral.200"
                          _focus={{
                            borderColor: 'primary.500',
                            boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                          }}
                          fontSize="14px"
                          h="40px"
                        />
                        {errors.farmLocation && (
                          <FormErrorMessage fontSize="12px">{errors.farmLocation}</FormErrorMessage>
                        )}
                      </FormControl>
                    </>
                  )}

                  {/* Officer-specific fields */}
                  {formData.role === 'officer' && (
                    <>
                      <Box h="1px" bg="neutral.200" w="full" />

                      <FormControl isRequired isInvalid={!!errors.farmLocation}>
                        <FormLabel fontSize="13px" fontWeight="600" color="neutral.900" mb={2}>
                          Enter Location
                        </FormLabel>
                        <Input
                          placeholder="E.g., Harare, Zimbabwe"
                          value={formData.farmLocation}
                          onChange={(e) => setFormData({
                            ...formData,
                            farmLocation: e.target.value
                          })}
                          borderColor="neutral.200"
                          _focus={{
                            borderColor: 'primary.500',
                            boxShadow: '0 0 0 1px rgba(26, 94, 63, 0.1)',
                          }}
                          fontSize="14px"
                          h="40px"
                        />
                        {errors.farmLocation && (
                          <FormErrorMessage fontSize="12px">{errors.farmLocation}</FormErrorMessage>
                        )}
                      </FormControl>
                    </>
                  )}

                  {/* Register Button */}
                  <Button
                    type="submit"
                    w="full"
                    h="40px"
                    bg="primary.500"
                    color="white"
                    fontWeight="600"
                    fontSize="14px"
                    borderRadius="8px"
                    _hover={{
                      bg: 'primary.600',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(26, 94, 63, 0.3)',
                    }}
                    _active={{
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 4px rgba(26, 94, 63, 0.2)',
                    }}
                    isLoading={isLoading}
                    transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    mt={2}
                  >
                    Create Account
                  </Button>
                </VStack>
              </form>

              {/* Divider */}
              <Box h="1px" bg="neutral.200" w="full" />

              {/* Login Link */}
              <VStack spacing={2} align="center">
                <Text fontSize="14px" color="neutral.600">
                  Already have an account?{' '}
                  <Link
                    as={RouterLink}
                    to="/login"
                    color="primary.500"
                    fontWeight="600"
                    _hover={{ color: 'primary.600', textDecoration: 'none' }}
                  >
                    Sign in
                  </Link>
                </Text>
              </VStack>
            </VStack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Register;
